import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Clock, X, Tag } from 'lucide-react';
import { MdArrowRightAlt } from 'react-icons/md';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import CheckoutForm from '../payments/CheckoutForm';
import { showError, showSuccess, showLoader, closeLoader } from '../../stores/slices/commonSlice';
import { setCountry } from '../../stores/slices/eventSlice';
import { baseUrl, StripePayment } from '../../constant/constant';
import { getCountryList } from '../../stores/actions/eventsActions';
import { getCookie, appendGuestTickets } from '../../utils/utils';
import { startCheckoutSession, clearCheckoutSession, getDeviceFingerprint, noteCouponAttempt } from '../../fraud';
import { computeCartFees, feeConfigFromCountryRow } from '../../utils/fees';
import useModalHeaderPadding from '../../hooks/useModalHeaderPadding';
import { setPurchasedTickets, showPurchaseSuccess } from '../../stores/slices/ticketSlice';

const hasStripeKey = typeof StripePayment.privateKey === 'string' && StripePayment.privateKey.trim() !== '';
const stripePromise = hasStripeKey ? loadStripe(StripePayment.privateKey) : null;

/**
 * How long a cart is held. Exported because the reservation spans the WHOLE
 * checkout — ticket selection and the payment form — and TicketSelectModal runs
 * the clock across both steps; this component only owns it when it is used
 * standalone (events page), where there is no earlier step.
 */
export const RESERVATION_SECONDS = 10 * 60;

/** m:ss for the countdown, shared with whichever surface renders the banner. */
export const formatTimer = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
export default function Payment(props) {
    const dispatch = useDispatch();
    const store = useStore();
    const navigate = useNavigate();
    // `expired` is the host-owned reservation signal: when it is a boolean the
    // surface around this component runs the clock (TicketSelectModal does, so
    // one countdown covers ticket selection AND payment) and this component just
    // follows it. Left null — the standalone modal on the events page — it runs
    // its own clock as before.
    const { openPayment, handleClose, embedded = false, onBack, hideTimer = false, hidePayFooter = false, onPayStateChange, children, couponPortalTarget = null, timerPortalTarget = null, onExpire, onExpiredDismiss, expired: hostExpired = null } = props;
    const hostOwnsTimer = typeof hostExpired === 'boolean';
    const checkoutFormId = embedded ? 'ticket-checkout-form' : 'payment-modal-checkout-form';

    const [feeConfig, setFeeConfig] = useState(null);
    const [currency, setCurrency] = useState();
    const [total, setTotal] = useState(0.0);
    const [button, setButton] = useState('Pay Now');
    const [disabled, setDisabled] = useState(false);
    // `disabled` doubles as "a payment attempt is in flight": CheckoutForm raises
    // it on submit and drops it again on every recoverable outcome (decline, OTP
    // challenge shown, popup blocked, duplicate). Mirrored into a ref so the
    // countdown below can read it without listing it as a dependency — adding it
    // to the effect's deps would restart the interval and reset the clock to a
    // full 10:00 on every pay attempt.
    const payInFlightRef = useRef(false);
    useEffect(() => {
        payInFlightRef.current = disabled;
    }, [disabled]);
    const [IPAddress, setIPAddress] = useState('');
    const [chargedIntentId, setChargedIntentId] = useState(null);
    const [serverAcked, setServerAcked] = useState(false);
    const [timeExpired, setTimeExpired] = useState(false);
    const [refundState, setRefundState] = useState('idle'); // idle|refunding|refunded|none
    const [closeHovered, setCloseHovered] = useState(false);
    // The card processor is resolved server-side per event (event -> country
    // default -> app default) and delivered on eventDetails.payment_gateway.
    // The VITE env only remains as a last-resort fallback for older API
    // responses that predate the field. Buyers no longer choose the gateway.
    const envGateway = import.meta.env.VITE_PAYMENT_GATEWAY;
    const fallbackGateway = envGateway === 'powertranz' || (envGateway === 'stripe' && hasStripeKey)
        ? envGateway
        : (hasStripeKey ? 'stripe' : 'powertranz');
    const [paymentGateway, setPaymentGateway] = useState(fallbackGateway);
    const { paddingStyle: modalHeaderPaddingStyle } = useModalHeaderPadding();
    const timerTextRef = useRef(null);
    // Stable idempotency token for a free order (no Stripe intent exists).
    // Held across retries of the same order; reset when the modal closes.
    const freeTokenRef = useRef(null);

    // Coupon state. `appliedDiscount` is the server-validated discount amount
    // applied to the ticket subtotal; the cart is reduced by it before fees are
    // computed (so fees + Stripe charge match the backend's redemption math).
    const [eventCoupons, setEventCoupons] = useState([]);
    const [couponInput, setCouponInput] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [appliedDiscount, setAppliedDiscount] = useState(0);
    const [couponBusy, setCouponBusy] = useState(false);
    const [couponError, setCouponError] = useState('');

    const eventDetails = useSelector((state) => state.event.eventDetails);
    const selectedEvent = useSelector((state) => state.event.selectTickets);
    const user = useSelector((state) => state.auth.user);
    const country = useSelector((state) => state.event.country);
    const getCountryFeeListDetails = useSelector(
        (state) => state.event.getCountryFeeListDetails
    );

    useEffect(() => {
        // Prefer the event's server-resolved fee settings (honors
        // custom_pricing_id) so the Stripe/PowerTranz charge amount matches what
        // the backend records at capture (GlobalSetting::resolveForEvent). Fall
        // back to the buyer's selected-country row for non-custom-priced events.
        const eventFee = eventDetails?.fee_settings;
        if (eventFee) {
            setFeeConfig(feeConfigFromCountryRow(eventFee));
            setCurrency(eventFee.currency);
            return;
        }
        if (!getCountryFeeListDetails) {
            dispatch(getCountryList());
            return;
        }
        const row = getCountryFeeListDetails.find((d) => d.country === country);
        if (!row) return;
        setFeeConfig(feeConfigFromCountryRow(row));
        setCurrency(row.currency);
    }, [eventDetails, getCountryFeeListDetails, country, dispatch]);

    // The paid ticket subtotal BEFORE any coupon — what a coupon discounts and
    // what we send to /coupons/validate.
    const originalSubtotal = useMemo(() => {
        const cart = selectedEvent?.selectedTicket || [];
        return cart.reduce((sum, it) => sum + Math.max(0, Number(it?.fee) || 0), 0);
    }, [selectedEvent]);

    // Cart with the applied coupon spread across PAID lines (proportional, last
    // line gets the rounding remainder). Mirrors PaymentController's split so the
    // displayed fees, the Stripe charge, and the recorded order all agree.
    const discountedCart = useMemo(() => {
        const round2 = (n) => Math.round(n * 100) / 100;
        const cart = selectedEvent?.selectedTicket || [];
        if (!appliedDiscount || appliedDiscount <= 0) return cart;

        const paidIdx = [];
        let paidSubtotal = 0;
        cart.forEach((it, i) => {
            const fee = Number(it?.fee) || 0;
            if (fee > 0) { paidIdx.push(i); paidSubtotal += fee; }
        });
        if (paidSubtotal <= 0) return cart;

        const discount = Math.min(appliedDiscount, paidSubtotal);
        const lineDisc = {};
        let allocated = 0;
        paidIdx.forEach((i, n) => {
            if (n === paidIdx.length - 1) {
                lineDisc[i] = round2(discount - allocated);
            } else {
                const d = round2(discount * ((Number(cart[i].fee) || 0) / paidSubtotal));
                allocated += d;
                lineDisc[i] = d;
            }
        });
        return cart.map((it, i) =>
            lineDisc[i] ? { ...it, fee: Math.max(0, (Number(it.fee) || 0) - lineDisc[i]) } : it
        );
    }, [selectedEvent, appliedDiscount]);

    // Per-config buyer breakdown (subtotal / serviceFee / paymentFee / total),
    // computed on the post-coupon cart. Mirrors the backend exactly so the
    // Stripe charge matches recorded orders.
    const feeBreakdown = useMemo(
        () => computeCartFees(discountedCart, feeConfig),
        [discountedCart, feeConfig]
    );

    // Recompute the charge + button whenever the cart or fees change.
    useEffect(() => {
        const t = feeBreakdown.total;
        setTotal(Number(t || 0).toFixed(2));
        // A $0 cart is a free order — no charge, so label the action button
        // accordingly instead of "Pay Now $0".
        setButton(Number(t || 0) > 0
            ? `Pay Now ${currency || ''} $${amountFormatter(t || 0)}`
            : 'Continue to Checkout');
    }, [feeBreakdown, currency]);

    // Load the event's public coupons when the (paid) modal opens, for the
    // browse-and-apply list. Free carts ($0) have nothing to discount, so skip.
    useEffect(() => {
        if (!openPayment) return;
        const eventId = eventDetails?.id;
        if (!eventId || originalSubtotal <= 0) { setEventCoupons([]); return; }

        let cancelled = false;
        fetch(`${baseUrl}/events/${eventId}/coupons`)
            .then((r) => r.json())
            .then((j) => { if (!cancelled) setEventCoupons(Array.isArray(j?.result) ? j.result : []); })
            .catch(() => { if (!cancelled) setEventCoupons([]); });
        return () => { cancelled = true; };
    }, [openPayment, eventDetails?.id, originalSubtotal]);

    // Resolve the card processor from the event (server-decided). Stripe is
    // only honoured when a publishable key is configured, otherwise we fall
    // back so checkout still works.
    useEffect(() => {
        const g = eventDetails?.payment_gateway;
        if (g === 'powertranz') {
            setPaymentGateway('powertranz');
        } else if (g === 'stripe') {
            setPaymentGateway(hasStripeKey ? 'stripe' : 'powertranz');
        } else {
            setPaymentGateway(fallbackGateway);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventDetails?.payment_gateway]);

    // Fraud flow, Stage 1: open a checkout session when the payment modal
    // opens so the server can capture signals (IP, device, country) and score
    // the attempt before any card is entered. Best-effort — a failure here
    // never blocks checkout (the token endpoints re-gate server-side anyway).
    //
    // Guarded by a ref that remembers which open+event we already started, so we
    // fire /fraud/start at most once per open — surviving React StrictMode's
    // double-invoke in dev and any unrelated re-render. Teardown is handled by
    // the closed branch below (the modal stays mounted; `openPayment` toggles),
    // NOT an effect cleanup, so StrictMode's transient unmount can't clear an
    // active session out from under us.
    const startedForKey = useRef(null);
    useEffect(() => {
        const key = openPayment && eventDetails?.id ? String(eventDetails.id) : null;

        // Modal closed (or no event yet): tear down the session we opened, once.
        if (!key) {
            if (startedForKey.current !== null) {
                startedForKey.current = null;
                clearCheckoutSession();
            }
            return;
        }

        // Already started for this open+event — do nothing.
        if (startedForKey.current === key) return;
        // Switched events while open — clear the previous session first.
        if (startedForKey.current !== null) clearCheckoutSession();

        startedForKey.current = key;
        const cart = selectedEvent?.selectedTicket || [];
        // A guest (user.id == 0) has no account email — theirs is the one they
        // typed on the contact step, carried on ticketUserDetails. Sending
        // `user?.email` here meant the fraud session for every guest checkout
        // was opened (or reused) with no email at all. Resolved the same way the
        // payment forms resolve the buyer email.
        const buyerEmail = (user?.id == 0)
            ? (selectedEvent?.ticketUserDetails?.[0]?.email || user?.email || '')
            : (user?.email || '');
        // Phone is deliberately not sent: /fraud/start rejects anything that
        // isn't bare E.164 and a 422 would lose the whole session. The contact
        // step supplies it, and syncCheckoutContact carries it onto an
        // already-open session.
        startCheckoutSession({
            event_id: eventDetails.id,
            user_id: user?.id,
            email: buyerEmail || undefined,
            currency,
            amount: Number(total) || 0,
            quantity: cart.length,
            tickets: cart,
        });
        // Re-start only when the modal opens or the event changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openPayment, eventDetails?.id]);

    // Validate + apply a coupon code (server is authoritative on the discount).
    const applyCoupon = async (rawCode) => {
        const code = String(rawCode || '').trim();
        if (!code || couponBusy) return;
        // Layer 3 — behavioural signal: repeated coupon attempts = coupon testing.
        noteCouponAttempt();
        setCouponBusy(true);
        setCouponError('');
        try {
            const res = await fetch(`${baseUrl}/coupons/validate`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(getCookie('_aps_t') ? { Authorization: `Bearer ${getCookie('_aps_t')}` } : {}),
                },
                body: JSON.stringify({
                    event_id: eventDetails?.id,
                    code,
                    subtotal: originalSubtotal,
                }),
            });
            const json = await res.json().catch(() => ({}));
            const result = json?.result;
            if (!res.ok || !result?.valid) {
                setAppliedCoupon(null);
                setAppliedDiscount(0);
                setCouponError(json?.message || 'That coupon could not be applied.');
                return;
            }
            setAppliedCoupon(result.coupon);
            setAppliedDiscount(Number(result.discount_amount) || 0);
            setCouponInput(result.coupon?.code || code);
            setCouponError('');
        } catch (_) {
            setCouponError('Could not validate the coupon. Please try again.');
        } finally {
            setCouponBusy(false);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setAppliedDiscount(0);
        setCouponInput('');
        setCouponError('');
    };

    // Fetch the user's public IP once for the audit log on the order row.
    useEffect(() => {
        fetch('https://api.ipify.org?format=json')
            .then((r) => r.json())
            .then((d) => setIPAddress(d.ip))
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!openPayment) {
            setTimeExpired(false);
            setChargedIntentId(null);
            setServerAcked(false);
            setRefundState('idle');
            // Drop the free-order idempotency token so the next order mints a
            // fresh one (retries of the SAME order keep it — see completeFreeOrder).
            freeTokenRef.current = null;
            // Clear any applied/typed coupon so it doesn't leak into the next cart.
            setCouponInput('');
            setAppliedCoupon(null);
            setAppliedDiscount(0);
            setCouponError('');
            if (timerTextRef.current) {
                timerTextRef.current.textContent = formatTimer(RESERVATION_SECONDS);
            }
            return undefined;
        }

        setTimeExpired(false);
        setChargedIntentId(null);
        setServerAcked(false);
        setRefundState('idle');

        // The host is counting down for both of its steps — running a second
        // clock here would restart at 10:00 the moment the buyer reached
        // payment, handing them more time than the reservation actually has.
        if (hostOwnsTimer) return undefined;

        let remaining = RESERVATION_SECONDS;
        const updateTimerDisplay = () => {
            if (timerTextRef.current) {
                timerTextRef.current.textContent = formatTimer(remaining);
            }
        };
        updateTimerDisplay();

        const id = setInterval(() => {
            // Hold the clock while a charge is in flight or a 3DS challenge is
            // open. Expiring mid-payment tore down a live authentication and
            // then refunded a charge that was about to succeed — the buyer is
            // committed at this point and is no longer sitting on a reservation
            // they might abandon. It resumes from where it stopped if the
            // attempt fails and they retry.
            if (payInFlightRef.current) return;

            remaining -= 1;
            updateTimerDisplay();
            if (remaining <= 0) {
                clearInterval(id);
                setTimeExpired(true);
                setDisabled(true);
                // The global MUI Backdrop sits at zIndex 1201 and is only
                // dropped in the `finally` of whichever request raised it, so a
                // request still in flight at expiry left the spinner covering
                // the expiry notice entirely. Drop it here; the owning request's
                // own closeLoader() is idempotent.
                dispatch(closeLoader());
            }
        }, 1000);

        return () => clearInterval(id);
    }, [openPayment]);

    // Host-owned clock: adopt its expiry, taking exactly the same actions the
    // internal countdown does when it reaches zero (lock the pay button, drop
    // the global loader that would otherwise cover the notice). Everything
    // downstream — the expiry overlay, the refund of an in-flight charge —
    // hangs off timeExpired and so is unchanged.
    useEffect(() => {
        if (!hostOwnsTimer || !openPayment || !hostExpired || timeExpired) return;
        setTimeExpired(true);
        setDisabled(true);
        dispatch(closeLoader());
    }, [hostOwnsTimer, hostExpired, openPayment, timeExpired]);

    // Tell the host surface the reservation is gone so it can take its own
    // chrome down. It must HIDE rather than unmount: this component lives inside
    // the checkout modal, so unmounting the host takes the expiry dialog and the
    // refund call below with it.
    useEffect(() => {
        if (timeExpired) onExpire?.();
    }, [timeExpired]);

    useEffect(() => {
        if (!openPayment || !timeExpired) return;

        if (!chargedIntentId || serverAcked) {
            setRefundState('none');
            return;
        }

        setRefundState('refunding');
        (async () => {
            try {
                const res = await fetch(`${baseUrl}/payment/cancel-and-refund`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ intent_id: chargedIntentId }),
                });
                const json = await res.json().catch(() => ({}));
                if (json?.has_order) {
                    setRefundState('none');
                } else if (json?.refunded) {
                    setRefundState('refunded');
                } else {
                    setRefundState('none');
                }
            } catch (_) {
                setRefundState('none');
            }
        })();
    }, [openPayment, timeExpired, chargedIntentId, serverAcked]);

    const stripeOptions = useMemo(
        () => ({
            mode: 'payment',
            amount: Math.round(total * 100),
            currency: currency ? currency.toLowerCase() : 'usd',
            appearance: {
                rules: {
                    '.Label': {
                        fontWeight: '500',
                    },
                },
            },
        }),
        [total, currency]
    );

    const surfaceSuccess = (jsonResponse) => {
        if (Array.isArray(jsonResponse?.tickets)) {
            dispatch(setPurchasedTickets(jsonResponse.tickets));
            // Guests (no bearer token) can't fetch /my-tickets, so persist their
            // purchased stubs locally to show in the Upcoming tab afterwards.
            if (!getCookie('_aps_t')) {
                appendGuestTickets(jsonResponse.tickets);
            }
        }
        if (jsonResponse?.message) {
            dispatch(showSuccess(jsonResponse.message));
        }
        // Mark the server side as acked BEFORE closing — the
        // reservation-expired effect reads this to decide whether a
        // refund call is needed if the timer hits 0 simultaneously.
        setServerAcked(true);
        // Purchase is complete — drop the checkout session token (and stop
        // telemetry) so a SECOND checkout by the same buyer opens a fresh
        // session instead of the reuse guard short-circuiting on the stale one.
        clearCheckoutSession();
        handleClose();
        clearAllState();
        // Confirm the purchase, then hand off to the tickets.
        //
        //   Desktop — route to My Tickets, raise "You're going!" over it.
        //   Mobile  — raise "You're going!" over the event page; its button
        //             opens the swipeable ticket viewer inline. No page load,
        //             and the QR is one tap away.
        //
        // Mobile used to skip the dialog entirely and snap straight to the
        // viewer. That left the moment of truth — "did that payment go
        // through?" — answered only by a toast that scrolls away, while the
        // buyer was dropped into a QR carousel with no statement that the
        // order completed. Both platforms now confirm first.
        //
        // Deferred a tick, as the old dialog's open was: navigating unmounts the
        // event page and this component with it, so the close + state reset
        // above have to finish first.
        setTimeout(() => {
            // Both conditions are read LIVE here rather than from render state,
            // because either one being stale silently downgrades mobile to the
            // desktop redirect — which is exactly how this broke:
            //
            //  - viewport: matchMedia at fire time, same breakpoint the viewer
            //    itself uses. A hook's value is whatever the last render saw.
            //  - tickets: from the STORE, not from this response. Not every
            //    success path carries ticket rows in its payload, but any of
            //    them may have populated purchased_tickets — and the viewer
            //    renders nothing without them (`if (!tickets.length) return
            //    null`), so it would have opened to a blank screen.
            //
            // No tickets anywhere ⇒ the page is the honest fallback.
            const isMobileNow =
                typeof window !== 'undefined' &&
                typeof window.matchMedia === 'function' &&
                window.matchMedia('(max-width:767px)').matches;
            const purchased = store.getState()?.tickets?.purchased_tickets || [];

            // Mobile: confirm in place. No navigate — the dialog's button
            // opens the viewer over this page, which is what kept the mobile
            // path fast in the first place.
            if (isMobileNow && purchased.length > 0) {
                dispatch(showPurchaseSuccess(true));
                return;
            }

            // Desktop: route to My Tickets and raise the success dialog over it.
            // The dialog answers the question the buyer actually has at this
            // moment — did the payment go through — and the page already behind
            // it is their ticket list, so dismissing it lands them somewhere
            // useful instead of back on the event they just bought. Its "View
            // My Ticket" button opens the QR viewer for anyone who wants it now.
            //
            // Both dialogs are mounted globally in App.jsx and driven by Redux,
            // so they survive the route change. My Tickets reads the same
            // purchased_tickets rows and leaves them alone while a viewer is
            // open, so nothing fights over that state.
            //
            // The dialog renders null with no rows, so with nothing purchased we
            // just land on the page, exactly as before.
            navigate('/my-tickets');
            if (purchased.length > 0) {
                dispatch(showPurchaseSuccess(true));
            }
        }, 0);
    };

    // Complete a FREE order. Free tiers have no charge, so there's no Stripe
    // PaymentIntent — we mint a stable client token (reused on retry so a
    // double-click can't create two order sets) and post straight to
    // /payment/success with gateway:'free'. The backend independently
    // re-validates every ticket is genuinely free before creating orders.
    const completeFreeOrder = async () => {
        if (disabled) return;
        setDisabled(true);
        setButton('Getting your ticket…');

        if (!freeTokenRef.current) {
            const rand = (typeof crypto !== 'undefined' && crypto.randomUUID)
                ? crypto.randomUUID().replace(/-/g, '')
                : `${IPAddress || ''}${performance.now()}${Math.random().toString(36).slice(2)}`.replace(/\W/g, '');
            freeTokenRef.current = `free_${rand}`;
        }
        const intentId = freeTokenRef.current;
        // Deliberately NOT setChargedIntentId: that flag drives the
        // reservation-expiry refund, and a free order has no charge to refund
        // (a free_* token would 400 the Stripe refund path).

        try {
            dispatch(showLoader());
            const res = await fetch(`${baseUrl}/payment/success`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    intent_id: intentId,
                    gateway: 'free',
                    event_id: eventDetails.id,
                    user_id: user.id,
                    tickets: selectedEvent,
                    country,
                    ip_address: IPAddress,
                }),
            });

            const jsonResponse = await res.json().catch(() => ({}));
            if (!res.ok || jsonResponse.status === 'failed') {
                throw new Error(jsonResponse.message || 'Could not complete your free order. Please try again.');
            }
            surfaceSuccess(jsonResponse);
        } catch (error) {
            dispatch(showError(error.message));
            setButton(error.message);
            setDisabled(false);
        } finally {
            dispatch(closeLoader());
        }
    };

    const pollPaymentStatus = (intentId, { intervalMs = 2000, timeoutMs = 30000 } = {}) =>
        new Promise((resolve, reject) => {
            const startedAt = Date.now();
            const tick = async () => {
                try {
                    const res = await fetch(`${baseUrl}/payment/status/${encodeURIComponent(intentId)}`);
                    const json = await res.json();
                    if (json?.status === 'complete') return resolve(json);
                    if (json?.status === 'failed')   return reject(new Error(json.message || 'Payment failed.'));
                } catch (_) {
                }
                if (Date.now() - startedAt >= timeoutMs) {
                    return reject(new Error('Still finalizing your order. Check My Tickets or your email shortly.'));
                }
                setTimeout(tick, intervalMs);
            };
            tick();
        });

    const processOrderAfterPayment = async (intentId) => {
        if (intentId && typeof intentId === 'object') {
            surfaceSuccess(intentId);
            return;
        }

        setChargedIntentId(intentId);
        const backendUrl = `${baseUrl}/payment/success`;
        const controller = new AbortController();
        const abortTimer = setTimeout(() => controller.abort(), 15000);

        dispatch(showLoader());
        try {
            const res = await fetch(backendUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    intent_id: intentId,
                    event_id: eventDetails.id,
                    user_id: user.id,
                    tickets: selectedEvent,
                    country,
                    ip_address: IPAddress,
                    // Server re-validates + redeems; null/absent when none applied.
                    coupon_code: appliedCoupon?.code || null,
                }),
                signal: controller.signal,
            });
            clearTimeout(abortTimer);

            if (!res.ok) {
                const err = new Error('Something went wrong. Please try again after sometime.');
                // Carried so the catch can tell a server fault (no verdict, the
                // order may exist) from an application-level failure.
                err.httpStatus = res.status;
                throw err;
            }
            const jsonResponse = await res.json();
            if (jsonResponse.status === 'failed') {
                throw new Error(jsonResponse.message);
            }

            surfaceSuccess(jsonResponse);
        } catch (error) {
            clearTimeout(abortTimer);

            // AbortError (timeout), generic network failure, or a 5xx → fall
            // back to polling. A PHP fatal (e.g. max_execution_time hit partway
            // through order creation) comes back as a 500 JSON from the API
            // exception handler, so fetch resolves and it is neither an abort
            // nor a TypeError — yet the order may be fully written. Treat it as
            // "no verdict" and poll. Other errors (4xx body with failed status,
            // explicit message) propagate as user-visible failures.
            const isTimeout = error?.name === 'AbortError';
            const isNetwork = error instanceof TypeError; // fetch network-level
            const isServerFault = [408, 500, 502, 503, 504].includes(error?.httpStatus);
            if (!isTimeout && !isNetwork && !isServerFault) {
                dispatch(showError(error.message));
                setButton(error.message);
                return;
            }

            setButton('Finalizing your tickets…');
            try {
                const polled = await pollPaymentStatus(intentId);
                surfaceSuccess(polled);
            } catch (pollErr) {
                dispatch(showError(pollErr.message));
                setButton(pollErr.message);
            }
        } finally {
            dispatch(closeLoader());
        }
    };

    // Stolen/lost card decline: blocklist the buyer/device in the BACKGROUND
    // and silently close the checkout. We never surface success or failure —
    // the buyer must not learn their payment method was blocklisted — so this
    // is fire-and-forget with no toast, and the modal just closes.
    const blacklist = (intentId) => {
        fetch(`${baseUrl}/payment/blacklist`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                intent_id: intentId,
                ip_address: IPAddress,
                user_id: user.id,
                email: user.email,
                device_id: getDeviceFingerprint(),
            }),
        }).catch(() => {});
        handleClose();
    };

    const updatePayButton = (val) => setButton(val);
    const updateDisabledButton = (val) => setDisabled(val);

    // When the pay footer is hidden (an embedded caller renders its own Pay Now
    // button, e.g. TicketSelectModal's summary sidebar), report the live button
    // label + disabled state up so that button can mirror the loading state
    // instead of sitting there static on the first click.
    useEffect(() => {
        if (typeof onPayStateChange === 'function') {
            onPayStateChange({ button, disabled });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [button, disabled]);
    const clearAllState = () => {
        setButton('Pay Now');
        setDisabled(false);
    };

    /* ------------------------------ helpers ------------------------------ */
    function amountFormatter(amount) {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(amount);
    }
    function amountFormatter2(amount) {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    }
    const cur = currency || '$';

    // Cart → grouped lines for the order summary (1x General, 2x VIP, etc.)
    const groupedTickets = (() => {
        const map = new Map();
        const items = selectedEvent?.selectedTicket || [];
        items.forEach((item) => {
            const key = item.name;
            if (!map.has(key)) {
                map.set(key, { name: item.name, count: 0, unitFee: parseFloat(item.fee) });
            }
            map.get(key).count += 1;
        });
        return Array.from(map.values());
    })();
    const ticketCount = (selectedEvent?.selectedTicket || []).length;
    // A $0 cart (free tier) needs a no-charge completion path instead of the
    // Stripe gateway block. Derived from feeBreakdown (synchronous) rather than
    // the `total` state so a paid cart never briefly shows the free button.
    const showFreeCompletion = ticketCount > 0 && Number(feeBreakdown?.total || 0) <= 0;
    // subtotal / serviceFee / paymentFee come from the shared per-config
    // calculator so the summary lines always sum to the charged total.
    // subtotal shown is the pre-coupon `originalSubtotal`; fees come from the
    // post-coupon breakdown so they reconcile with the charged total.
    const { serviceFee, paymentFee } = feeBreakdown;

    const countryOptions = (getCountryFeeListDetails || []).map((r) => r.country);

    const orderSummarySection =
        ticketCount > 0 ? (
            <div className="w-full !mt-8 mb-6 pt-[22px] pb-4 px-4 bg-[#f7f7f8] border border-[#eeeeee] rounded-2xl box-border">
                <h4 className="m-0 mb-2 text-lg font-bold leading-6 text-[#0f172a]">Order Summary</h4>
                {eventDetails?.name ? (
                    <p className="m-0 mb-3 text-base font-bold leading-tight text-center text-[#0f172a] break-words">
                        {eventDetails.name}
                    </p>
                ) : null}
                <div className="h-px bg-[#e6e7ea] w-full" aria-hidden="true" />

                <div className="flex flex-col">
                    {groupedTickets.map((t) => (
                        <div key={t.name} className="flex items-center justify-between min-h-12">
                            <span className="text-base font-semibold text-[#1e293b]">
                                {t.count}x {t.name}
                            </span>
                            <span className="text-base font-bold text-[#1e293b]">
                                {cur} ${amountFormatter(t.count * t.unitFee)}
                            </span>
                        </div>
                    ))}
                </div>
                <div className="h-px bg-[#e6e7ea] w-full" aria-hidden="true" />

                <div className="flex flex-col gap-3 py-3">
                    <div className="flex items-center justify-between">
                        <span className="text-[15px] font-medium text-[#475569]">
                            Subtotal ({ticketCount} {ticketCount === 1 ? 'Ticket' : 'Tickets'})
                        </span>
                        <span className="text-[15px] font-semibold text-[#475569]">
                            {cur} ${amountFormatter(originalSubtotal)}
                        </span>
                    </div>
                    {appliedDiscount > 0 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[15px] font-medium text-emerald-600">
                                Coupon{appliedCoupon?.code ? ` (${appliedCoupon.code})` : ''}
                            </span>
                            <span className="text-[15px] font-semibold text-emerald-600">
                                − {cur} ${amountFormatter(appliedDiscount)}
                            </span>
                        </div>
                    )}
                    {serviceFee > 0 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[15px] font-medium text-[#475569]">Service Fee</span>
                            <span className="text-[15px] font-semibold text-[#475569]">
                                {cur} ${amountFormatter(serviceFee)}
                            </span>
                        </div>
                    )}
                    {paymentFee > 0 && (
                        <div className="flex items-center justify-between">
                            <span className="text-[15px] font-medium text-[#475569]">Processing Fee</span>
                            <span className="text-[15px] font-semibold text-[#475569]">
                                {cur} ${amountFormatter(paymentFee)}
                            </span>
                        </div>
                    )}
                </div>
                <div className="h-px bg-[#e6e7ea] w-full" aria-hidden="true" />

                <div className="flex items-center justify-between mt-4">
                    <span className="text-[22px] font-bold text-[#0f172a] leading-tight">Total</span>
                    <span className="text-[20px] font-extrabold text-[#0f172a] leading-none tracking-tight">
                        {cur} ${amountFormatter(total)}
                    </span>
                </div>
            </div>
        ) : null;

    // Coupon browse-and-apply panel. Only meaningful for a paid cart (a $0/free
    // cart has nothing to discount), so it's hidden when originalSubtotal <= 0.
    const couponSection =
        ticketCount > 0 && originalSubtotal > 0 ? (
            <div className="w-full">
                {appliedCoupon ? (
                    // Applied state: a solid violet card with the savings + remove.
                    <div className="flex items-center justify-between rounded-2xl border-2 border-[#cdc6f7] bg-[#f4f2fe] px-4 py-3.5">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <Tag className="w-5 h-5 text-[#6c5ce7] shrink-0" aria-hidden="true" />
                            <div className="min-w-0">
                                <p className="m-0 text-sm font-extrabold text-[#3f3d56] truncate">
                                    {appliedCoupon.code}
                                </p>
                                <p className="m-0 text-xs font-semibold text-[#6c5ce7]">
                                    {appliedCoupon.label || 'Coupon applied'} · −{cur} ${amountFormatter(appliedDiscount)}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={removeCoupon}
                            className="shrink-0 ml-3 text-sm font-bold text-[#6c5ce7] bg-transparent border-0 cursor-pointer hover:underline"
                        >
                            Remove
                        </button>
                    </div>
                ) : (
                    // Empty state: dashed violet box, tag icon, input + Apply.
                    <div className="rounded-2xl border-2 border-dashed border-[#cdc6f7] bg-white px-4 py-4">
                        <div className="flex items-center gap-2 mb-3">
                            <Tag className="w-[18px] h-[18px] text-[#6c5ce7] shrink-0" aria-hidden="true" />
                            <span className="text-[15px] font-bold text-[#3f3d56]">Have a coupon code?</span>
                        </div>

                        {/* Browsable public coupons for this event (tap to apply). */}
                        {eventCoupons.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {eventCoupons.map((c) => (
                                    <button
                                        key={c.id}
                                        type="button"
                                        disabled={couponBusy}
                                        onClick={() => applyCoupon(c.code)}
                                        title={c.label}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[#cdc6f7] bg-[#f4f2fe] px-3 py-1.5 cursor-pointer disabled:opacity-60 hover:border-[#6c5ce7]"
                                    >
                                        <span className="text-xs font-extrabold text-[#3f3d56]">{c.code}</span>
                                        <span className="text-[11px] font-semibold text-[#6c5ce7]">{c.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Manual code entry (also works for private/unlisted codes). */}
                        <div className="flex items-stretch gap-2 rounded-xl border border-[#e5e3f0] bg-white pl-3 pr-1.5 py-1">
                            <input
                                type="text"
                                value={couponInput}
                                onChange={(e) => setCouponInput(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') applyCoupon(couponInput); }}
                                placeholder="Enter coupon code"
                                className="flex-1 min-w-0 bg-transparent border-0 outline-none text-sm font-semibold text-[#3f3d56] placeholder:text-[#9ca3af] placeholder:font-medium py-2"
                            />
                            <button
                                type="button"
                                disabled={couponBusy || !couponInput.trim()}
                                onClick={() => applyCoupon(couponInput)}
                                className="shrink-0 px-4 text-sm font-bold text-[#6c5ce7] bg-transparent border-0 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {couponBusy ? '…' : 'Apply'}
                            </button>
                        </div>

                        {couponError && (
                            <p className="m-0 mt-2 text-xs font-semibold text-red-500">{couponError}</p>
                        )}
                    </div>
                )}
            </div>
        ) : null;

    const closeButtonStyle = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        border: 'none',
        cursor: 'pointer',
        backgroundColor: closeHovered ? 'rgba(0, 0, 0, 0.1)' : 'transparent',
        color: '#111827',
        transition: 'background-color 0.2s ease',
    };

    const dismissExpired = () => {
        setTimeExpired(false);
        setRefundState('idle');
        setChargedIntentId(null);
        setServerAcked(false);
        clearAllState();
        // The host hid itself on expiry, so returning to step 1 would leave it
        // hidden and unreachable — it has to do the full close instead.
        if (onExpiredDismiss) onExpiredDismiss();
        else if (embedded && onBack) onBack();
        else handleClose();
    };

    const payFooter = total > 0 ? (
        <div className={`shrink-0 border-t border-[#ececec] pt-4 px-4 pb-4 bg-white ${embedded ? '' : ''}`}>
            <button
                type="submit"
                form={checkoutFormId}
                disabled={disabled}
                className="flex items-center justify-center w-full h-[52px] border-0 rounded-2xl bg-[#ffe500] shadow-[0_4px_14px_rgba(0,0,0,0.1)] outline-none cursor-pointer px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <span className="text-base font-extrabold text-black leading-none">{button}</span>
                <MdArrowRightAlt className="ml-1.5 text-[22px] text-black shrink-0" aria-hidden="true" />
            </button>
        </div>
    ) : null;

    const freeFooter = showFreeCompletion ? (
        <div className="shrink-0 border-t border-[#ececec] pt-4 px-4 pb-4 bg-white">
            <button
                type="button"
                onClick={completeFreeOrder}
                disabled={disabled}
                className="flex items-center justify-center w-full h-[52px] border-0 rounded-2xl bg-[#ffe500] shadow-[0_4px_14px_rgba(0,0,0,0.1)] outline-none cursor-pointer px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
                <span className="text-base font-extrabold text-black leading-none">{button}</span>
                <MdArrowRightAlt className="ml-1.5 text-[22px] text-black shrink-0" aria-hidden="true" />
            </button>
        </div>
    ) : null;

    // The countdown owns a ref, not state, so ticking it never re-renders the
    // Stripe/PowerTranz card fields underneath (which would drop focus mid-typing).
    const timerBanner = (
        <div className="flex shrink-0 items-center justify-center h-[38px] bg-[#fff2f2]">
            <p className="m-0 text-sm font-semibold text-[#e53935] leading-none inline-flex items-center gap-1.5">
                <span className="text-sm leading-none" aria-hidden="true">⏳</span>
                Tickets reserved for <span ref={timerTextRef}>{formatTimer(RESERVATION_SECONDS)}</span>
            </p>
        </div>
    );

    // Three placements, in priority order:
    //   1. a portal target (the checkout modal pins it under its own header, so
    //      it stays visible instead of scrolling away with the payment card),
    //   2. inline at the top of the panel (standalone payment modal),
    //   3. nowhere — hideTimer with no target supplied.
    // Before this, the checkout passed hideTimer and rendered nothing of its
    // own, so the reservation counted down invisibly and the buyer's first
    // notice of it was the expiry overlay.
    const renderedTimer = timerPortalTarget
        ? createPortal(timerBanner, timerPortalTarget)
        : (!hideTimer ? timerBanner : null);

    // The expiry notice used to be absolutely positioned inside whichever
    // container held it, which put it UNDER everything that outranks that
    // container's stacking context:
    //   - the global MUI loader Backdrop  (zIndex 1201)
    //   - the fraud OTP challenge          (z-[1000])
    //   - embedded: it was scoped to the Payment <section>, a card partway down
    //     a scrollable checkout, so it could sit entirely off-screen.
    // Portalled to <body> at z-[1300] it clears all of them in both surfaces.
    // Sized and styled to match the modal's own alert screens (Checkout
    // Restricted / Leave Checkout?): dimmed backdrop, one compact card, and the
    // shared tokens — #0f172a headings, gray-500 body, h-12 rounded-xl #ffe500
    // CTA. It was a full-bleed white sheet with its own oversized type scale,
    // which read as a different product once the checkout hid behind it.
    const expiredOverlay = timeExpired ? (
        <div
            className="fixed inset-0 z-[1300] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm sm:p-6 animate-fade-in"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="reservation-expired-title"
            aria-describedby="reservation-expired-desc"
        >
            <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col items-center text-center px-6 py-8 sm:px-10 sm:py-10 animate-pop-in">
                <span className="flex items-center justify-center w-16 h-16 rounded-full bg-red-50">
                    <Clock className="w-8 h-8 text-red-500" />
                </span>

                <h2 id="reservation-expired-title" className="m-0 mt-5 text-2xl sm:text-[26px] font-extrabold text-[#0f172a]">
                    Time Expired
                </h2>
                <p id="reservation-expired-desc" className="m-0 mt-3 text-sm sm:text-[15px] font-medium text-gray-500 leading-relaxed">
                    Your ticket reservation has expired. Please select your tickets to start over.
                </p>

                {refundState === 'refunding' && (
                    <p className="m-0 mt-4 text-sm font-bold text-gray-500">Refunding your payment…</p>
                )}
                {refundState === 'refunded' && (
                    <p className="m-0 mt-4 text-sm font-bold text-green-600">Your payment has been refunded.</p>
                )}

                <button
                    type="button"
                    onClick={dismissExpired}
                    disabled={refundState === 'refunding'}
                    className="mt-8 w-full max-w-sm h-12 rounded-xl border-none bg-[#ffe500] font-bold text-[#0f172a] text-base cursor-pointer hover:brightness-95 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:brightness-100"
                >
                    OK
                </button>
            </div>
        </div>
    ) : null;

    const checkoutPanel = (
        <>
            {renderedTimer}

            <div className={`flex flex-col flex-1 min-h-0 bg-white space-y-5 [scrollbar-width:thin] ${embedded ? 'pt-0 px-0 pb-0' : 'pt-7 px-6 pb-0'}`}>
                {/* When a portal target is supplied (checkout's Order Summary
                    column) the coupon widget renders there instead of inline. */}
                {embedded && !couponPortalTarget && couponSection}
                {embedded && couponPortalTarget && createPortal(couponSection, couponPortalTarget)}
                {embedded && children}

                {total > 0 && (
                    <>
                        {/* Scotia card-processing notice, carried over from main
                            (58c8a01) — the modal it was added to was rewritten
                            here. Lives inside the `total > 0` branch so it shows
                            wherever card entry renders (modal and embedded) and
                            stays out of the free-ticket flow. */}
                        {/* <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                            <p className="m-0 text-sm text-[#0f172a]">
                                Notice: We are currently experiencing technical difficulties with{' '}
                                <img src="/scotia.png" alt="Scotia" className="inline-block w-20 align-middle" />{' '}
                                card processing. Please use an alternative payment method.
                            </p>
                        </div> */}

                        {/* Card processor is resolved server-side per event; buyers
                            no longer choose it. Driven by `paymentGateway`. */}
                        <Elements stripe={stripePromise} options={stripeOptions}>
                            <CheckoutForm
                                key={paymentGateway}
                                formId={checkoutFormId}
                                externalFooter
                                total={total}
                                paymentSuccess={processOrderAfterPayment}
                                blackList={blacklist}
                                onBlocked={() => {
                                    // Drop the cached session token on a block so
                                    // the buyer's NEXT checkout re-opens a fresh
                                    // /fraud/start (which re-scores the blacklist
                                    // and returns blocked) instead of the reuse
                                    // guard short-circuiting on the stale token —
                                    // otherwise a mid-tab blacklisted buyer keeps
                                    // sailing back into checkout untracked.
                                    clearCheckoutSession();
                                    handleClose();
                                }}
                                updatePayButton={updatePayButton}
                                updateDisabledButton={updateDisabledButton}
                                button={button}
                                disabled={disabled}
                                selectedEvent={selectedEvent}
                                paymentGateway={paymentGateway}
                                couponCode={appliedCoupon?.code || null}
                                cancelled={timeExpired}
                            />
                        </Elements>
                    </>
                )}

                {!embedded && couponSection}
                {!embedded && orderSummarySection}
            </div>

            {embedded && !hidePayFooter && payFooter}
            {embedded && !hidePayFooter && freeFooter}

            {expiredOverlay && createPortal(expiredOverlay, document.body)}
        </>
    );

    /* ------------------------------ render ------------------------------- */
    if (!openPayment) return null;

    if (embedded) {
        return (
            <div className="relative flex flex-col flex-1 min-h-0 overflow-hidden w-full">
                {checkoutPanel}
            </div>
        );
    }

    return (
        // Same rule as the embedded host: hidden, not unmounted, so the expiry
        // dialog (portalled to <body>) and the refund call outlive it.
        <div
            className={`fixed inset-0 z-[150] flex items-end sm:items-center justify-center sm:p-6 ${timeExpired ? 'invisible pointer-events-none' : ''}`}
            aria-hidden={timeExpired || undefined}
        >
            <div
                className="hidden sm:block absolute inset-0 sm:bg-black/30 transition-opacity"
                onClick={handleClose}
                aria-hidden
            />

            <div
                className="relative bg-white w-full h-[100dvh] sm:h-auto sm:max-w-lg sm:rounded-[2rem] sm:max-h-[85vh] shadow-2xl flex flex-col z-10 overflow-hidden animate-rt-slide-up sm:animate-rt-pop-in"
                role="dialog"
                aria-modal="true"
                aria-labelledby="payment-modal-title"
            >
                <style>{`
                    @keyframes rt-slide-up { 0% { transform: translateY(100%); } 100% { transform: translateY(0); } }
                    @keyframes rt-pop-in   { 0% { opacity: 0; transform: scale(0.95); } 100% { opacity: 1; transform: scale(1); } }
                    .animate-rt-slide-up { animation: rt-slide-up 0.4s ease-out; }
                    .animate-rt-pop-in   { animation: rt-pop-in 0.4s ease-out; }
                `}</style>

                {/* ----- Header ----- */}
                <div
                    className="flex items-center justify-between bg-[#ffea00] sm:border-b sm:border-gray-100 shrink-0"
                    style={modalHeaderPaddingStyle}
                >
                    <h2
                        id="payment-modal-title"
                        className="text-xl sm:text-2xl font-black text-black"
                    >
                        Payments
                    </h2>
                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        style={closeButtonStyle}
                        onMouseEnter={() => setCloseHovered(true)}
                        onMouseLeave={() => setCloseHovered(false)}
                        className="p-1.5 sm:p-2 focus:outline-none"
                    >
                        <X size={24} />
                    </button>
                </div>

                {checkoutPanel}
                {payFooter}
                {freeFooter}
            </div>
        </div>
    );
}
